import React, { useContext, useCallback } from "react";
import { SharedTimelineContext } from "../SharedTimelineContext";
import { getTimelinePosition } from "../../dashboard/utils/timeline";
import { getTodayPositionForYearRange, isCurrentYearInRange, currentDate } from "../../dashboard/utils/date";
import { ProgressBar } from "../../dashboard/components/ProgressBar";
import { TaskBar } from "../../dashboard/components/TaskBar";
import KeyEventMarkers from "../components/KeyEventMarkers";
import { getSharedMilestonePosition } from "../../dashboard/utils/milestone";
import { SharedTimelineMilestoneMarker } from "../components/SharedTimelineMilestoneMarker";
import { DependencyLines } from "../../dashboard/components/DependencyLine";
import moment from "moment";

const empty = () => {};

function ProgramTaskBar({ task, viewType, displayYearRange, dynamicWidths, onTaskClick, scopeID, scopeName, projectID, projectName }) {
  const startDate = task.startDate ? moment(task.startDate).toDate() : null;
  const endDate = task.endDate ? moment(task.endDate).toDate() : null;
  if (!startDate || !endDate) return null;

  const { left, width, shouldRender } = getTimelinePosition(
    startDate,
    endDate,
    viewType,
    currentDate,
    displayYearRange,
    dynamicWidths
  );

  if (!shouldRender) return null;

  return (
    <TaskBar
      task={task}
      left={left + 5}
      width={width}
      critical={false}
      handleDragStart={empty}
      handleAddDependency={empty}
      handleTaskModal={() => onTaskClick({ taskId: task.id, scopeID, scopeName, projectID, projectName })}
      handleMarkCompleted={empty}
      handleDuplicateTask={empty}
      setActivePopup={empty}
      isHideTask={false}
      isReadOnly={true}
    />
  );
}

const KEY_EVENT_TYPES = {
  TARGET_DATE: 0,
  DATE_RANGE: 1,
};

export default function Timeline({
  reference,
  viewType,
  componentHeight,
  isHide,
  expandedProjects,
  entityType,
  displayYearRange,
  hoveredRowId,
  setHoveredRowId,
  keyEvents = [],
  dynamicWidths,
  expandedScopes,
  onTaskClick,
}) {
  const { timelineData, timelineMilestones, settings } = useContext(SharedTimelineContext);

  // Get nested items based on entity type
  const getNestedItems = (item) => {
    if (entityType === "portfolio") {
      return item.projects || [];
    }
    // For program timeline, get scopeItems
    return item.scopeItems || item.scopes || [];
  };

  const getTimelineLength = () => {
    let length = timelineData?.length * 52;
    for (const item of timelineData) {
      if (expandedProjects[item.id]) {
        const nestedItems = getNestedItems(item);
        length += (nestedItems?.length || 0) * 52;
        if (entityType === "program" && expandedScopes) {
          for (const nestedItem of nestedItems) {
            if (expandedScopes[nestedItem.id]) {
              length += (nestedItem.tasks?.length || 0) * 20;
            }
          }
        }
      }
    }
    return length;
  };

  // Determine height calculation based on entity type
  const getHeightStyle = () => {
    if (viewType === "day") {
      return `calc(100vh - ${entityType === "portfolio" ? "246" : "305"}px)`;
    }
    return `calc(100vh - ${entityType === "portfolio" ? "271" : "300"}px)`;
  };

  // Group milestones by position and row (program/project)
  const groupMilestones = useCallback(() => {
    const grouped = {};

    timelineMilestones?.forEach((milestone) => {
      const position = getSharedMilestonePosition(milestone.targetDate, viewType, displayYearRange, dynamicWidths);
      if (position === null) return;

      if (entityType === "portfolio") {
        const programId = milestone.program?.id;
        const projectId = milestone.project?.id;
        
        if (!programId || !projectId) return;

        // Determine which row to show the milestone on
        const isProgramExpanded = expandedProjects[programId];
        const rowId = isProgramExpanded ? projectId : programId;
        
        const key = `${rowId}-${position}`;
        if (!grouped[key]) {
          grouped[key] = {
            rowId,
            position,
            milestones: [],
            isProgramLevel: !isProgramExpanded,
          };
        }
        grouped[key].milestones.push(milestone);
      } else if (entityType === "program") {
        const projectId = milestone.project?.id;
        if (!projectId) return;

        const key = `${projectId}-${position}`;
        if (!grouped[key]) {
          grouped[key] = {
            rowId: projectId,
            position,
            milestones: [],
            isProgramLevel: false,
          };
        }
        grouped[key].milestones.push(milestone);
      }
    });

    return Object.values(grouped);
  }, [timelineMilestones, viewType, displayYearRange, entityType, expandedProjects, dynamicWidths]);

  // Calculate row positions
  const getRowPosition = useCallback((rowId, isProgramLevel) => {
    let topPosition = 0;

    for (const item of timelineData) {
      // Check if this is the target row
      if (item.id === rowId) {
        const nestedItems = getNestedItems(item);
        let rowHeight;
        if (isProgramLevel) {
          rowHeight = 52;
        } else if (expandedProjects[item.id]) {
          let nestedHeight = 0;
          for (const nestedItem of nestedItems) {
            nestedHeight += 52;
            if (entityType === "program" && expandedScopes?.[nestedItem.id]) {
              nestedHeight += (nestedItem.tasks?.length || 0) * 20;
            }
          }
          rowHeight = 52 + nestedHeight;
        } else {
          rowHeight = 52;
        }
        return { topPosition, rowHeight, found: true };
      }

      topPosition += 52;

      // Add nested items height if expanded
      if (expandedProjects[item.id]) {
        const nestedItems = getNestedItems(item);
        for (const nestedItem of nestedItems) {
          if (nestedItem.id === rowId) {
            return { topPosition, rowHeight: 52, found: true };
          }
          topPosition += 52;
          // Account for task rows under this scope (program type)
          if (entityType === "program" && expandedScopes?.[nestedItem.id]) {
            for (const task of nestedItem.tasks || []) {
              if (task.id === rowId) {
                return { topPosition, rowHeight: 20, found: true };
              }
              topPosition += 20;
            }
          }
        }
      }
    }

    return { topPosition: 0, rowHeight: 52, found: false };
  }, [timelineData, expandedProjects, expandedScopes, entityType, getNestedItems]);

  const groupedMilestones = groupMilestones();

  // Build flat list of visible tasks (for dependency lines) — program only
  const visibleTasks = React.useMemo(() => {
    if (entityType !== "program") return [];
    const result = [];
    for (const item of timelineData) {
      if (!expandedProjects[item.id]) continue;
      const scopes = item.scopeItems || item.scopes || [];
      for (const scope of scopes) {
        if (!expandedScopes?.[scope.id]) continue;
        for (const task of scope.tasks || []) {
          result.push(task);
        }
      }
    }
    return result;
  }, [entityType, timelineData, expandedProjects, expandedScopes]);

  // Filter only RANGE events for KeyEventMarkers (TARGET_DATE events are now in the top row)
  const rangeKeyEvents = React.useMemo(() => {
    if (entityType === "portfolio" && keyEvents && keyEvents.length > 0) {
      return keyEvents.filter(event => event.type === KEY_EVENT_TYPES.DATE_RANGE);
    }
    return keyEvents;
  }, [entityType, keyEvents]);

  return (
    <div
      ref={reference}
      className="relative overflow-y-auto overflow-x-hidden timeline-container"
      style={{
        height: getHeightStyle(),
      }}
    >
      {viewType !== "day" && isCurrentYearInRange(displayYearRange?.start, displayYearRange?.end) && (
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-[#342C82] z-1"
          style={{
            left: `${getTodayPositionForYearRange(viewType, displayYearRange?.start, displayYearRange?.end) - 12}px`,
            minHeight: '100vh',
          }}
        >
          <div className="relative -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#342C82] rotate-45" />
        </div>
      )}

      {/* Render key event markers - Only RANGE events now, TARGET_DATE events are in top row */}
      {rangeKeyEvents && rangeKeyEvents.length > 0 && entityType === "portfolio" && (
        <KeyEventMarkers
          keyEvents={rangeKeyEvents}
          timelineData={timelineData}
          viewType={viewType}
          displayYearRange={displayYearRange}
          expandedProjects={expandedProjects}
          dynamicWidths={dynamicWidths}
        />
      )}

      {/* Render milestone markers */}
      {viewType !== "day" && groupedMilestones.map((group, index) => {
        const { topPosition, rowHeight, found } = getRowPosition(group.rowId, group.isProgramLevel);
        if (!found) return null;

        // For program timeline, show line when project is expanded
        const hasLine = entityType === "program" && expandedProjects[group.rowId];

        return (
          <SharedTimelineMilestoneMarker
            key={`milestone-group-${index}`}
            milestones={group.milestones}
            position={group.position}
            rowTopPosition={topPosition}
            rowHeight={rowHeight}
            settings={settings}
            hasLine={hasLine}
          />
        );
      })}

      {timelineData.map((item, index) => (
        <div key={item.id} className="border-b">
          <div
            onMouseEnter={() => setHoveredRowId(item.id)}
            onMouseLeave={() => setHoveredRowId(null)}
            className={`h-[52px] relative ${
              index % 2 === 0 ? "bg-white" : "bg-gray-50"
            } ${hoveredRowId === item.id && "!bg-[#ece7fd]"}`}
          >
            <ProgressBar
              scope={item}
              getTimelinePosition={getTimelinePosition}
              viewType={viewType}
              displayYearRange={displayYearRange}
              dynamicWidths={dynamicWidths}
            />
          </div>
          {expandedProjects[item.id] &&
            getNestedItems(item)?.map((nestedItem, i) => (
              <div key={nestedItem.id}>
                <div
                  onMouseEnter={() => setHoveredRowId(nestedItem.id)}
                  onMouseLeave={() => setHoveredRowId(null)}
                  className={`h-[52px] relative ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } ${hoveredRowId === nestedItem.id && "!bg-[#ece7fd]"}`}
                >
                  <ProgressBar
                    scope={nestedItem}
                    getTimelinePosition={getTimelinePosition}
                    viewType={viewType}
                    displayYearRange={displayYearRange}
                    dynamicWidths={dynamicWidths}
                  />
                </div>
                {/* Task bars — only for program timeline when scope is expanded */}
                {entityType === "program" && expandedScopes?.[nestedItem.id] &&
                  nestedItem.tasks?.map((task) => (
                    <div
                      key={task.id}
                      onMouseEnter={() => setHoveredRowId(task.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      className={`h-[20px] relative ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      } ${hoveredRowId === task.id && "!bg-[#ece7fd]"}`}
                    >
                      <ProgramTaskBar
                        task={task}
                        viewType={viewType}
                        displayYearRange={displayYearRange}
                        dynamicWidths={dynamicWidths}
                        onTaskClick={onTaskClick}
                        scopeID={nestedItem.id}
                        scopeName={nestedItem.name}
                        projectID={item.id}
                        projectName={item.name}
                      />
                    </div>
                  ))}
              </div>
            ))}
        </div>
      ))}

      {entityType === "program" && visibleTasks.length > 0 && (
        <DependencyLines
          tasks={visibleTasks}
          taskDates={visibleTasks}
          viewType={viewType}
          getHeight={getTimelineLength}
        />
      )}
    </div>
  );
}
